import { useState, useMemo, useEffect } from 'react';
import React from 'react';
import '../../styles/PageCommon.css';
import './PromoCalendarPage.css';
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/arrow-down-icon.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar-icon.svg';
import {
  getUpcomingMonths,
  getPromoWeekRange,
  formatIntoIsoDate,
  formatToReadableDate,
  formatPromoDateRange,
} from '../../utils/utils';
import { promoService } from '../../services/promoService';
import { useFilterParams } from '../../hooks/useFilterParams';
import { AccordionCheckboxSelector } from '../../components/common/AccordionCheckboxSelector/AccordionCheckboxSelector';
import SegmentedControl from '../../components/common/SegmentedControl/SegmentedControl';
import LoadingAnimation from '../../components/common/LoadingAnimation/LoadingAnimation';
import { NotificationContainer } from '../../components/common/NotificationContainer';
import NotificationModal from '../../components/common/NotificationModal';
import { parseApiError } from '../../utils/parseErrorMessage';
import CalendarModal from '../../components/common/CalendarModal/CalendarModal';
import ExpandableList from '../../components/common/ExpandableList/ExpandableList';

// Default range: the current month through the same month a year later.
const now = new Date();
const DEFAULT_DATE_FROM = formatIntoIsoDate(new Date(now.getFullYear(), now.getMonth(), 1));
const DEFAULT_DATE_TO = formatIntoIsoDate(new Date(now.getFullYear(), now.getMonth() + 12, 0));

const DEFAULT_FILTERS = {
  date_from: DEFAULT_DATE_FROM,
  date_to: DEFAULT_DATE_TO,
  view_type: "DSP",
  sku_code: [],
  brand: [],
  category: [],
  sub_category: [],
  subline: [],
  branch_name: [],
  promo_channel: [],
};

// Placeholder channel colours, cycled per channel group for channel cells and
// as a fallback when a promo does not include a valid promo_color.
const CHANNEL_PLACEHOLDER_COLORS = [
  { background: '#c8dff8', color: '#0c447c' },
  { background: '#c0dd97', color: '#27500a' },
  { background: '#fac775', color: '#633806' },
  { background: '#e0dfd8', color: '#5f5e5a' },
  { background: '#f8c8d4', color: '#7c0c2f' },
  { background: '#d9c8f8', color: '#3a157c' },
];

const getChannelColor = (index) =>
  CHANNEL_PLACEHOLDER_COLORS[index % CHANNEL_PLACEHOLDER_COLORS.length];

const normalizeHexColor = (value) => {
  if (typeof value !== 'string') return null;

  const match = value.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return null;

  const hex = match[1];
  const fullHex = hex.length === 3
    ? hex.split('').map((char) => char + char).join('')
    : hex;

  return `#${fullHex}`;
};

const getReadableTextColor = (hexColor) => {
  const red = parseInt(hexColor.slice(1, 3), 16);
  const green = parseInt(hexColor.slice(3, 5), 16);
  const blue = parseInt(hexColor.slice(5, 7), 16);
  const yiq = (red * 299 + green * 587 + blue * 114) / 1000;

  return yiq >= 150 ? '#010101' : '#ffffff';
};

const getPromoColor = (promo, fallbackColor) => {
  const background = normalizeHexColor(promo?.promo_color);

  if (!background) return fallbackColor;

  return {
    background,
    color: getReadableTextColor(background),
  };
};

// An empty week cell, with a divider on every week boundary.
function emptyWeekCell(week) {
  return (
    <td
      key={`empty-${week}`}
      className="branch-cell"
      style={{ borderRight: "1px solid #DEDEDE" }}
    />
  );
}

// Render the week cells for a single promo row. The promo's span is drawn as
// one continuous cell (colSpan across its whole range, no padding) so the bar
// stays connected across weeks and months with no gaps between them.
function renderPromoWeekCells(promo, range, color, totalWeeks) {
  if (!range) {
    return Array.from({ length: totalWeeks }, (_, week) => emptyWeekCell(week));
  }

  const cells = [];

  for (let week = 0; week < range.start; week++) {
    cells.push(emptyWeekCell(week));
  }

  cells.push(
    <td
      key={`bar-${range.start}`}
      className="branch-cell"
      colSpan={range.end - range.start + 1}
      style={{
        padding: "12px 0px",
        borderRight: "1px solid #DEDEDE",
      }}
    >
      <div className="g-bar bar-full" style={color}>
        <span className="g-bar-name">{promo.promo_name}</span>
        <span className="g-bar-dates">{formatPromoDateRange(promo.promo_start_date, promo.promo_end_date)}</span>
      </div>
    </td>
  );

  for (let week = range.end + 1; week < totalWeeks; week++) {
    cells.push(emptyWeekCell(week));
  }

  return cells;
}


export default function PromoCalendarPage() {
  // const [downloading, setDownloading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataError, setDataError] = useState("");
  
  const [availableChannels, setAvailableChannels] = useState([]);
  const [promoData, setPromoData] = useState([]);

  const [isCalendarFilterOpen, setIsCalendarFilterOpen] = useState(false);
  const [isProductsFilterOpen, setIsProductsFilterOpen] = useState(false);

  const [branchOptions, setBranchOptions] = useState([]);
  const [skuOptions, setSkuOptions] = useState([]);

  // --- Filters State and Logic ---
  const { filters, updateFilters } = useFilterParams(DEFAULT_FILTERS);
  const productFiltersArray = Object.values({
    sku_code:     filters.sku_code     || [],
    brand:        filters.brand        || [],
    category:     filters.category     || [],
    sub_category: filters.sub_category || [],
    subline:      filters.subline      || [],
  }).flat();

  const fetchPromoCalendar = () => {
    setIsLoadingData(true);
    promoService.getPromoCalendar(filters)
      .then(data => {
        if (!data) return;
        // The endpoint may return either a bare array of promos or an object
        // wrapping it (e.g. { rows: [...] }).
        const rows = Array.isArray(data)
          ? data
          : (data.rows ?? data.promos ?? data.items ?? data.data);
        setPromoData(Array.isArray(rows) ? rows : []);
        setBranchOptions(data.branch_options || []);
        setSkuOptions(data.sku_code_options || []);
      })
      .catch(error => setDataError(parseApiError(error) || ""))
      .finally(() => setIsLoadingData(false));
  };

  useEffect(() => {
    fetchPromoCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // const handleDownloadExcel = () => {
  //   setDownloading(true);
  // };

  const upcomingMonths = useMemo(
    () => getUpcomingMonths(filters.date_from, filters.date_to),
    [filters.date_from, filters.date_to]
  );
  const totalWeeks = upcomingMonths.length * 4;
  const weeks = Array.from({ length: totalWeeks }, (_, i) => i);

  // First day of the first upcoming month — the anchor for week positioning.
  const baseDate = useMemo(() => {
    const start = filters.date_from ? new Date(filters.date_from) : new Date();
    return new Date(start.getFullYear(), start.getMonth(), 1);
  }, [filters.date_from]);

  // Currently selected channel (tabs are single-select); null means "all".
  const activeChannel = (filters.promo_channel || [])[0] || null;

  // Refresh the full channel list only while viewing all channels, so picking a
  // tab (which narrows promoData server-side) doesn't drop the other tabs.
  useEffect(() => {
    if (!activeChannel) {
      const channels = [...new Set((promoData || []).map((p) => p.promo_channel).filter(Boolean))];
      setAvailableChannels(channels);
    }
  }, [promoData, activeChannel]);

  // Group promos by channel, preserving first-seen order, so the channel cell
  // can span all rows that share the same promo_channel.
  const channelGroups = useMemo(() => {
    const map = new Map();
    (promoData || []).forEach((promo) => {
      const key = promo.promo_channel || "—";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(promo);
    });
    return Array.from(map.entries());
  }, [promoData]);

  return (
    <>
      {isLoadingData && <LoadingAnimation />}
      <NotificationContainer>
        {
          dataError && <NotificationModal closeModal={() => setDataError(null)} type="error" subTitle={dataError} title="Ошибка" duration={3000}/>
        }
      </NotificationContainer>
    <div className="page-content promo-calendar-page">
      <div className="page-header-container">
        <h1 className="promo-calendar-title">Промо-календарь</h1>
      </div>

      <div className="promo-calendar-fliters-row dp-chart-filters-container">

        <div className='tabs-container'>
          <button className={`tabs-item-container ${!activeChannel ? 'active' : ''}`}
            onClick={() => updateFilters({ promo_channel: null })}>
            <div className={`tabs-item ${!activeChannel ? 'active' : ''}`}>Все</div>
          </button>
          {
            availableChannels.map((channel) => (
              <button key={channel}
                className={`tabs-item-container ${activeChannel === channel ? 'active' : ''}`}
                onClick={() => updateFilters({ promo_channel: [channel] })}>
                <div className={`tabs-item ${activeChannel === channel ? 'active' : ''}`}>{channel}</div>
              </button>
            ))
          }
        </div>

        <button
          onClick={() => setIsProductsFilterOpen(true)}
          className="filter-select"
        >
          <span className="filter-select-label">Продукты:</span>
          <span className="filter-select-option">
            {productFiltersArray.join(", ")}
          </span>
          {
            productFiltersArray.length > 0
            && <span style={{ background: "#7CC805", color: "#fff", borderRadius: 20, fontSize: 11, fontWeight: 700, padding: "1px 6px", flexShrink: 0 }}>
              {productFiltersArray.length}
            </span>
          }
          <ArrowDownIcon size={24} color='#7CC805'/>
        </button>
        {
          isProductsFilterOpen &&
            <AccordionCheckboxSelector
              selectedOptions={(({ page, page_size, view_type, branch_name, promo_channel, ...rest }) => rest)(filters)}
              filterOptions={productFiltersArray}
              closeModal={() => setIsProductsFilterOpen(false)}
              onApply={(selectedFilter) => {updateFilters(selectedFilter)}}
            />
        }

        <SegmentedControl
          value={filters.view_type?.toLowerCase()}
          onChange={(value) => updateFilters({ view_type: value })}
          options={[
            {
              label: "Деньги",
              options: [
                { label: "Прайс-лист", value: "dsp" },
                { label: "Инвойс цена", value: "invoice price" },
              ],
            },
            { label: "Короб", value: "cases" },
            {
              label: "Вес",
              options: [
                { label: "Брутто", value: "gross weight" },
                { label: "Нетто", value: "net weight" },
              ],
            },
          ]}
        />

        <button
          onClick={() => {console.log("isCalendarFilterOpen ", isCalendarFilterOpen);setIsCalendarFilterOpen(true)}}
          className="filter-select"
        >              
          <CalendarIcon size={16} color='#7CC805'/>
          <span className="filter-select-label">{formatToReadableDate(filters.date_from)} - {formatToReadableDate(filters.date_to)}</span>
          <ArrowDownIcon size={24} color='#7CC805'/>
        </button>
        {
          isCalendarFilterOpen && 
            <CalendarModal title={"Выберите период"} 
              isOpen={isCalendarFilterOpen} 
              closeModal={()=>{setIsCalendarFilterOpen(false)}} 
              dateRange={{ startDate: filters.date_from, endDate: filters.date_to }} 
              applyDateRangeFilter={(dateRange) => { 
                updateFilters({ date_from: dateRange.startDate, date_to: dateRange.endDate }); 
              }}
              // minDate={availableDateRange?.minDate}
              // maxDate={availableDateRange?.maxDate}
            />
        }

      </div>

      <div className="table-outer-wrapper outer-scrollbar">
        <div className="sticky-table-wrapper custom-scrollbar">
          <table className="sticky-table promo-calendar-table">
            <thead>
              <tr>
                <th rowSpan={2} className="sticky left l1" style={{borderTopLeftRadius: "20px", boxShadow: "-12px -20px 0px 0px #f8f9fa", borderLeft: "1px solid #DEDEDE"}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: 6, }}>
                    <span>Канал</span>
                    {/* {'sku_code' in branchesFilterOptions && <ColumnFilterDropdown label="Код СКЮ" options={branchesColOptions['sku_code'] || []} selected={getBranchesColSelected('sku_code')} onApply={(vals) => handleBranchesColFilterApply('sku_code', vals)} />} */}
                  </div>
                </th>
                <th rowSpan={2} className="sticky left l2">
                  <div style={{display: 'flex', alignItems: 'center', gap: 6, }}>
                    <span>Код СКЮ</span>
                    {/* {'sku_name' in branchesFilterOptions && <ColumnFilterDropdown label="Наименование" options={branchesColOptions['sku_name'] || []} selected={getBranchesColSelected('sku_name')} onApply={(vals) => handleBranchesColFilterApply('sku_name', vals)} />} */}
                  </div>
                </th>
                <th rowSpan={2} className="sticky left l3">
                  <div style={{display: 'flex', alignItems: 'center', gap: 6, }}>
                    <span>Склады</span>
                    {/* {'mother_sku' in branchesFilterOptions && <ColumnFilterDropdown label="Базовый СКЮ" options={branchesColOptions['mother_sku'] || []} selected={getBranchesColSelected('mother_sku')} onApply={(vals) => handleBranchesColFilterApply('mother_sku', vals)} />} */}
                  </div>
                </th>
                {
                  upcomingMonths.map((month, index) => (
                    <th className="" 
                      colSpan={4}
                      style={{
                        // top: "45px", 
                        // borderRadius: "0px",
                        borderRight: "1px solid #DEDEDE",
                      }} 
                      key={index}
                    >
                      <div className="branches-title">
                          {month}
                      </div>
                    </th>
                  ))
                }
              </tr>
  
              <tr>
                {
                  weeks.map((week, index) => (
                    <th className="week-cell-header" style={{
                      top: "45px", 
                      // borderRadius: "0px",
                      borderLeft: "0px",
                      borderRight: "1px solid #DEDEDE",
                      borderTop: "1px solid #DEDEDE",
                      borderRadius: "0px",
                    }} key={week}>
                      Н{(week % 4) + 1}
                    </th>
                  ))
                }
              </tr>
            </thead>
  
            <tbody>
              {channelGroups.map(([channel, promos], groupIndex) => {
                const channelColor = getChannelColor(groupIndex);
                return promos.map((promo, rowIndex) => (
                  <tr key={promo.promo_id ?? `${channel}-${rowIndex}`}>
                    {rowIndex === 0 && (
                      <td className="sticky left l1" rowSpan={promos.length} style={channelColor}>
                        {channel}
                      </td>
                    )}
                    <td className="sticky left l2">
                      <ExpandableList options={skuOptions} selectedOptions={promo.promo_sku_codes || []} />
                    </td>
                    <td className="sticky left l3">
                      {
                        promo.promo_branches_all ? "Все" : <ExpandableList options={branchOptions} selectedOptions={promo.promo_branches || []} />
                      }
                      
                    </td>
                    {renderPromoWeekCells(promo, getPromoWeekRange(promo, baseDate, totalWeeks), getPromoColor(promo, channelColor), totalWeeks)}
                  </tr>
                ));
              })}
              {channelGroups.length === 0 && (
                <tr>
                  <td className="sticky left l1" />
                  <td className="sticky left l2" />
                  <td className="sticky left l3" />
                  {
                    weeks.map((week, index) => (
                      <td key={index} className="branch-cell"
                        style={{
                          borderRight: "1px solid #DEDEDE",
                        }}
                      />
                    ))
                  }
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
    </>
  );
}
